import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { ProjectStatus } from '@/types/project';
import { PROJECT_GROUPS } from '@/constants/projectGroups';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

// Hook to get project groups from actual projects
export const useAccessibleProjectGroups = () => {
  const { data: projectGroups = [], isLoading, error } = useQuery({
    queryKey: ['accessible-project-groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('project_group')
        .not('project_group', 'is', null)
        .neq('project_group', '')
        .limit(500);
      
      if (error) throw error;
      
      // Get unique project groups
      const uniqueGroups = [...new Set(data?.map(item => item.project_group).filter(Boolean))];
      return uniqueGroups.sort();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: []
  });

  return {
    data: projectGroups,
    isLoading,
    error
  };
};

// Hook to get project types that user can access
export const useAccessibleProjectTypes = () => {
  const { data: projectTypes = [], isLoading, error } = useQuery({
    queryKey: ['accessible-project-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_types')
        .select('name')
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data?.map(item => item.name) || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: []
  });

  return {
    data: projectTypes,
    isLoading,
    error
  };
};

// Hook to get statuses that user can access
export const useAccessibleStatuses = (restrictedProjectIds?: string[]) => {
  const { data: userProjects = [], isLoading, error } = useQuery({
    queryKey: ['accessible-statuses', restrictedProjectIds],
    queryFn: async () => {
      let query = supabase
        .from('projects')
        .select('status')
        .neq('status', 'inactive')
        .neq('status', 'service')
        .limit(500);
      
      // Filter by restrictedProjectIds if provided - use chunked query for large arrays
      if (restrictedProjectIds && restrictedProjectIds.length > 0) {
        // Use chunkedInQuerySimple for restrictedProjectIds which can exceed 100
        const projectData = await chunkedInQuerySimple<{ status: string }>(
          supabase,
          'projects',
          'id',
          restrictedProjectIds,
          'status',
          (q) => q.neq('status', 'inactive').neq('status', 'service')
        );
        return projectData || [];
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: []
  });

  const statusLabels: Record<ProjectStatus, string> = {
    'intake': 'Intake',
    'tender': 'Tender', 
    'build': 'Build',
    'defects': 'Defects',
    'service': 'Service',
    'archive': 'Archive',
    'inactive': 'Inactive'
  };

  const accessibleStatuses = useMemo(() => {
    const uniqueStatuses = [...new Set(
      userProjects
        .map(p => p.status)
        .filter((status): status is ProjectStatus => status !== null && status !== undefined)
    )];
    
    return uniqueStatuses
      .filter(status => status !== 'service' && status !== 'inactive')
      .map(status => ({
        value: status,
        label: statusLabels[status] || status
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [userProjects]);

  return {
    data: accessibleStatuses,
    isLoading,
    error
  };
};

// Hook to get team leads from project team assignments (simplified)
export const useAccessibleTeamLeads = () => {
  const { data: teamLeads = [], isLoading, error } = useQuery({
    queryKey: ['accessible-team-leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_team_leads')
        .select(`
          user_id,
          profiles!inner(
            id,
            full_name,
            email
          )
        `);
      
      if (error) throw error;
      
      // Get unique team leads with deduplication
      const uniqueLeads = new Map();
      
      (data || []).forEach(teamLead => {
        const profile = teamLead?.profiles;
        if (profile?.id && !uniqueLeads.has(profile.id)) {
          uniqueLeads.set(profile.id, {
            id: profile.id,
            name: profile.full_name || profile.email || 'Unknown',
            email: profile.email
          });
        }
      });
      
      return Array.from(uniqueLeads.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 45 * 60 * 1000, // 45 minutes (team leads change less frequently)
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    retry: 2,
    placeholderData: []
  });

  return {
    data: teamLeads,
    isLoading,
    error
  };
};

// Hook to get users with project roles (for filter dropdown)
// Only returns users who have at least one project role assigned
export const useProjectRoleUsers = () => {
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['project-role-users'],
    queryFn: async () => {
      // Step 1: Get all permissions with non-empty project_roles
      const { data: permissions, error: permError } = await supabase
        .from('project_user_permissions')
        .select('user_id, project_roles')
        .not('project_roles', 'eq', '{}')
        .limit(1000);
      
      if (permError) throw permError;
      if (!permissions || permissions.length === 0) return [];
      
      // Step 2: Get unique user IDs
      const userIds = [...new Set(permissions.map(p => p.user_id))];
      
      // Step 3: Get profiles for these users using chunked query
      const profiles = await chunkedInQuerySimple<{ id: string; full_name: string | null; email: string | null; avatar_url: string | null }>(
        supabase,
        'profiles',
        'id',
        userIds,
        'id, full_name, email, avatar_url'
      );
      
      // Map profiles to return format
      return (profiles || [])
        .map(profile => ({
          id: profile.id,
          name: profile.full_name || profile.email || 'Unknown',
          email: profile.email,
          avatar_url: profile.avatar_url
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: []
  });

  return {
    data: users,
    isLoading,
    error
  };
};

// Hook to get available project role types (SOL, PM, CD, etc.) from actual data
export const useProjectRoleTypes = () => {
  const { data: roleTypes = [], isLoading, error } = useQuery({
    queryKey: ['project-role-types'],
    queryFn: async () => {
      // Get all project_roles from project_user_permissions
      const { data: permissions, error } = await supabase
        .from('project_user_permissions')
        .select('project_roles')
        .not('project_roles', 'eq', '{}')
        .limit(1000);
      
      if (error) throw error;
      
      // Collect all unique role types
      const roleSet = new Set<string>();
      (permissions || []).forEach(p => {
        const roles = p.project_roles as string[] | null;
        if (Array.isArray(roles)) {
          roles.forEach(role => roleSet.add(role));
        }
      });
      
      return Array.from(roleSet).sort();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    placeholderData: []
  });

  return {
    data: roleTypes,
    isLoading,
    error
  };
};